/**
 * `/procurement/payments` — vendor-payments ledger (S16a · purchase-side mirror of S17).
 *
 * V1.0 scope: read-only table of payments (newest first), tenant-scoped via
 * RLS. Primary entry to RECORD a payment is from the bill detail page; this
 * surface is for browsing the history.
 *
 * Chrome follows the prototype design system (page-head + accent-stripe KPI
 * tiles + table-card pattern). Avatar + sub-line treatment matches the bills
 * + invoices lists. Filters (search / mode / date-range) drive the
 * `vendorPayments.list` query directly.
 *
 * @see prototype/cash/payments.html — S16a baseline
 * @see prototype/README.md — V1 React route mapping
 * @see docs/spec/brand-spec.md §2 (colors) · §6 (cards) · §11 (layout)
 */
import { useEffect, useMemo, useState } from "react";
import type React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Calendar,
  CreditCard,
  Download,
  ListChecks,
  Search,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  ArrowRight,
  Printer,
  X,
} from "lucide-react";
import {
  Alert,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@flint/ui";
import { trpc } from "../../../../lib/trpc.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { formatDate } from "../../../../lib/format.ts";
import { openDocPdf } from "../../../../lib/pdf.ts";
import {
  avatarClassFor,
  initialsFor,
  DataTableCard,
  ListPagination,
} from "../../../../features/master-list/index.ts";

export const Route = createFileRoute("/(workspace)/procurement/payments/")({
  component: PaymentsPage,
});

const ALL_MODES = ["neft", "rtgs", "imps", "upi", "card", "cheque", "cash", "other"] as const;
type Mode = (typeof ALL_MODES)[number];

const RANGE_PRESETS = {
  all: { label: "All time", dateFrom: undefined, dateTo: undefined },
  this_month: { label: "This month", dateFrom: firstOfMonth(), dateTo: undefined },
  last_30: { label: "Last 30 days", dateFrom: daysAgo(30), dateTo: undefined },
  last_90: { label: "Last 90 days", dateFrom: daysAgo(90), dateTo: undefined },
} as const;
type RangeKey = keyof typeof RANGE_PRESETS;

function PaymentsPage() {
  // Filter state — local to the page; URL-search-params upgrade is V1.0.x.
  const [searchInput, setSearchInput] = useState("");
  const [mode, setMode] = useState<Mode | "">("");
  const [range, setRange] = useState<RangeKey>("this_month");

  // Debounce the search so we don't fire on every keystroke. 300ms is the
  // standard debounce for ledger search per invoice list precedent.
  const debouncedSearch = useDebounced(searchInput, 300);

  // Cursor-stack pagination (the shared list pattern — see vendors/PO lists).
  const PAGE_SIZE = 50;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];
  // Reset to page 1 whenever the result set changes (any filter moves).
  useEffect(() => {
    setCursorStack([]);
  }, [debouncedSearch, mode, range]);

  const queryInput = useMemo(() => {
    const preset = RANGE_PRESETS[range];
    const input: {
      limit: number;
      search?: string;
      mode?: Mode;
      dateFrom?: string;
      dateTo?: string;
      cursor?: string;
    } = { limit: PAGE_SIZE };
    if (debouncedSearch.trim()) input.search = debouncedSearch.trim();
    if (mode) input.mode = mode;
    if (preset.dateFrom) input.dateFrom = preset.dateFrom;
    if (preset.dateTo) input.dateTo = preset.dateTo;
    if (cursor !== undefined) input.cursor = cursor;
    return input;
  }, [debouncedSearch, mode, range, cursor]);

  const list = trpc.vendorPayments.list.useQuery(queryInput, {
    placeholderData: (prev) => prev,
  });
  const items = list.data?.items ?? [];

  // Pagination derivations — page index = cursor-stack depth + 1.
  const pageNumber = cursorStack.length + 1;
  const showingFrom = items.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + items.length;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    const next = list.data?.nextCursor;
    if (next) setCursorStack((s) => [...s, next]);
  };

  const paidItems = items.filter((p) => p.status === "paid");
  const totals = paidItems.reduce(
    (acc, r) => ({
      gross: acc.gross + BigInt(r.grossPaise),
      tds: acc.tds + BigInt(r.tdsPaise),
      net: acc.net + BigInt(r.netPaise),
    }),
    { gross: 0n, tds: 0n, net: 0n },
  );
  const vendorCount = new Set(paidItems.map((p) => p.vendorId)).size;
  const awaitingCount = items.filter((p) => p.status === "awaiting_approval").length;
  const hasActiveFilter =
    mode !== "" || range !== "this_month" || debouncedSearch.trim().length > 0;

  // Footer stat-bar derivations — mirror bills' "Avg bill · Top vendor · Recurring …"
  // line. We aggregate over PAID rows only (gross/tds/net are zero on
  // awaiting rows and would skew the averages).
  const avgPaymentPaise = paidItems.length > 0 ? totals.gross / BigInt(paidItems.length) : 0n;
  const topMode = topByCount(paidItems.map((p) => p.mode));
  const topVendor = topByCount(paidItems.map((p) => p.vendorName));

  return (
    <WorkspaceShell
      breadcrumbs={[
        { label: "Procurement" },
        { label: "Bills", to: "/procurement/bills" },
        { label: "Payments ledger" },
      ]}
    >
      <div className="flex flex-col gap-[18px]">
        {/* Page header */}
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
              Payments
            </h1>
            <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted">
              <span>
                <span className="num font-semibold text-ink">
                  {formatRupeesShort(totals.gross)}
                </span>{" "}
                paid · {RANGE_PRESETS[range].label.toLowerCase()}
              </span>
              <Sep />
              <span>
                <span className="num">{paidItems.length}</span> payments · {vendorCount} vendors
              </span>
              <Sep />
              {totals.tds > 0n ? (
                <span className="text-amber-700 font-semibold">
                  {formatRupees(totals.tds)} TDS withheld
                </span>
              ) : (
                <span>No TDS this period</span>
              )}
              {awaitingCount > 0 ? (
                <>
                  <Sep />
                  <span className="text-rose-700 font-semibold">
                    {awaitingCount} awaiting your OTP
                  </span>
                </>
              ) : null}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm font-semibold border border-line bg-paper text-ink hover:bg-bg hover:border-line-2 transition-colors"
            >
              <Download size={13} strokeWidth={2} />
              Export
            </button>
            <Link
              to="/procurement/bills"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-sm font-semibold border border-ink bg-ink text-paper hover:bg-ink-2 transition-colors"
              title="Record a payment from any bill's detail page"
            >
              <ArrowRight size={13} strokeWidth={2.4} />
              Go to a bill
            </Link>
          </div>
        </header>

        {/* KPI strip */}
        <section
          className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3"
          aria-label="Payment summary"
        >
          <KpiTile
            accent="emerald"
            label={`Gross paid · ${RANGE_PRESETS[range].label.toLowerCase()}`}
            value={formatRupeesShort(totals.gross)}
            meta={`across ${vendorCount} ${vendorCount === 1 ? "vendor" : "vendors"} · ${paidItems.length} ${paidItems.length === 1 ? "payment" : "payments"}`}
            icon={<ListChecks size={13} strokeWidth={2} />}
          />
          <KpiTile
            accent="amber"
            label="TDS withheld"
            value={formatRupees(totals.tds)}
            meta={
              totals.tds > 0n
                ? "Per vendor TDS section · captured at payment time"
                : "No section-mapped vendors paid yet"
            }
            icon={<ShieldCheck size={13} strokeWidth={2} />}
          />
          <KpiTile
            accent="indigo"
            label="Net to vendors"
            value={formatRupeesShort(totals.net)}
            meta={
              <>
                <DeltaPill kind="dn" label={`−${formatRupees(totals.tds)}`} /> after withholding
              </>
            }
            icon={<TrendingDown size={13} strokeWidth={2} />}
          />
          <KpiTile
            accent={awaitingCount > 0 ? "rose" : "muted"}
            label="Awaiting founder OTP"
            value={awaitingCount.toString()}
            valueUnit="queued"
            meta={
              awaitingCount > 0
                ? "Approval inbox holds the payload until founder OTP"
                : "Inbox empty"
            }
            icon={<TrendingUp size={13} strokeWidth={2} />}
          />
        </section>

        {/* Toolbar — chrome matches `bills/list/bill-filter-bar.tsx`
         *  (the @flint/ui Input + SelectTrigger primitives at h-10,
         *  icon-in-trigger, fixed-width selects). Keep these flush with
         *  bills + customers so list pages feel like one family. */}
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative h-10 min-w-0 w-full flex-1 sm:min-w-80 lg:max-w-[38rem]">
            <Search
              size={15}
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
            />
            <Input
              type="search"
              aria-label="Search payments"
              placeholder="Search vendor or reference…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-10 pl-9"
            />
          </div>

          <FilterSelect
            value={mode === "" ? "all" : mode}
            onValueChange={(next) => setMode(next === "all" ? "" : (next as Mode))}
            icon={<CreditCard size={14} />}
            ariaLabel="Filter payments by mode"
            placeholder="Payment mode"
            widthClassName="!w-44"
            active={mode !== ""}
          >
            <SelectItem value="all">All modes</SelectItem>
            {ALL_MODES.map((m) => (
              <SelectItem key={m} value={m}>
                {m.toUpperCase()}
              </SelectItem>
            ))}
          </FilterSelect>

          <FilterSelect
            value={range}
            onValueChange={(next) => setRange(next as RangeKey)}
            icon={<Calendar size={14} />}
            ariaLabel="Filter payments by date range"
            placeholder="Date range"
            widthClassName="!w-44"
            active={range !== "this_month"}
          >
            {Object.entries(RANGE_PRESETS).map(([k, v]) => (
              <SelectItem key={k} value={k}>
                {v.label}
              </SelectItem>
            ))}
          </FilterSelect>

          {hasActiveFilter ? (
            <button
              type="button"
              onClick={() => {
                setSearchInput("");
                setMode("");
                setRange("this_month");
              }}
              className="inline-flex items-center gap-1 text-xs text-sky-700 font-semibold hover:underline px-1"
            >
              <X size={11} strokeWidth={2.4} />
              Clear filters
            </button>
          ) : null}
        </div>

        {/* Table card */}
        {list.isLoading ? (
          <p className="text-sm text-muted">Loading payments…</p>
        ) : list.error ? (
          <Alert intent="bad" title="Couldn't load payments" description={list.error.message} />
        ) : items.length === 0 ? (
          <EmptyState hasFilter={hasActiveFilter} />
        ) : (
          <DataTableCard
            footer={
              items.length > 0
                ? [
                    {
                      label: "Avg payment",
                      value: paidItems.length > 0 ? formatRupeesShort(avgPaymentPaise) : "—",
                    },
                    {
                      label: "TDS withheld",
                      value: totals.tds === 0n ? "—" : formatRupeesShort(totals.tds),
                    },
                    {
                      label: "Top mode",
                      value: topMode
                        ? `${topMode.key.toUpperCase()} · ${topMode.count} payment${topMode.count === 1 ? "" : "s"}`
                        : "—",
                    },
                    {
                      label: "Top vendor",
                      value: topVendor
                        ? `${topVendor.key} · ${topVendor.count} payment${topVendor.count === 1 ? "" : "s"}`
                        : "—",
                    },
                    { label: "Awaiting OTP", value: `${awaitingCount} queued` },
                  ]
                : []
            }
            pagination={
              items.length > 0 ? (
                <ListPagination
                  pageNumber={pageNumber}
                  showingFrom={showingFrom}
                  showingTo={showingTo}
                  sortLabel={`sorted by paid date${hasActiveFilter ? " · filtered" : ""}`}
                  hasPrev={cursorStack.length > 0}
                  hasNext={!!list.data?.nextCursor}
                  onPrev={handlePrev}
                  onNext={handleNext}
                />
              ) : undefined
            }
          >
            <table className="w-full min-w-[980px] text-sm">
              <thead className="bg-bg">
                <tr className="text-left text-xs font-semibold uppercase tracking-wider text-muted border-b border-line">
                  <th className="px-3 py-2.5 font-semibold">Bill #</th>
                  <th className="px-4 py-2.5 font-semibold">Vendor</th>
                  <th className="px-3 py-2.5 font-semibold">Paid on</th>
                  <th className="px-3 py-2.5 font-semibold">Mode</th>
                  <th className="px-3 py-2.5 font-semibold">Reference</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Gross</th>
                  <th className="px-3 py-2.5 font-semibold text-right">TDS</th>
                  <th className="px-3 py-2.5 font-semibold text-right">Net</th>
                  <th className="px-3 py-2.5 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-b border-line last:border-b-0 hover:bg-bg">
                    {/* Bill # — links to bill detail; "+ N more" for multi-allocation */}
                    <td className="px-3 py-3 align-middle">
                      <BillCell bills={p.bills} />
                    </td>

                    {/* Vendor — rounded-md palette avatar + sub-line (city · TDS section) */}
                    <td className="px-4 py-3 align-middle">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span
                          aria-hidden
                          className={`grid h-8 w-8 shrink-0 place-items-center rounded-md text-xs font-bold ${avatarClassFor(p.vendorName)}`}
                        >
                          {initialsFor(p.vendorName)}
                        </span>
                        <div className="min-w-0">
                          <Link
                            to="/procurement/vendors/$vendorId"
                            params={{ vendorId: p.vendorId }}
                            className="block truncate font-semibold text-ink hover:underline"
                          >
                            {p.vendorName}
                          </Link>
                          <p className="truncate text-xs text-muted">{vendorSubLine(p)}</p>
                        </div>
                      </div>
                    </td>

                    <td className="px-3 py-3 align-middle font-mono text-muted">
                      {formatDate(p.paidOn)}
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-sm border border-line bg-bg text-ink-2 text-xs font-semibold uppercase tracking-wider font-mono">
                        {p.mode}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <span className="num text-xs text-muted truncate inline-block max-w-[200px]">
                        {p.reference || "—"}
                      </span>
                    </td>
                    <td className="px-3 py-3 align-middle text-right num">
                      {formatRupees(BigInt(p.grossPaise))}
                    </td>
                    <td className="px-3 py-3 align-middle text-right num">
                      {p.tdsPaise === "0" ? (
                        <span className="text-muted">—</span>
                      ) : (
                        <span className="text-amber-700">{formatRupees(BigInt(p.tdsPaise))}</span>
                      )}
                    </td>
                    <td className="px-3 py-3 align-middle text-right num font-semibold">
                      {formatRupees(BigInt(p.netPaise))}
                    </td>
                    <td className="px-3 py-3 align-middle">
                      <div className="flex items-center justify-end gap-2">
                        <StatusPill status={p.status} />
                        {p.status === "paid" ? (
                          <button
                            type="button"
                            onClick={() => openDocPdf(`/api/vendor-payments/${p.id}/pdf`)}
                            title="Print payment voucher"
                            aria-label="Print payment voucher"
                            className="grid h-7 w-7 shrink-0 place-items-center rounded-sm text-muted transition hover:bg-bg hover:text-ink"
                          >
                            <Printer size={14} strokeWidth={2} />
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTableCard>
        )}
      </div>
    </WorkspaceShell>
  );
}

// ── Bill cell ──────────────────────────────────────────────────────

function BillCell({ bills }: { bills: Array<{ id: string; docNumber: string | null }> }) {
  if (bills.length === 0) {
    return <span className="text-muted text-xs">—</span>;
  }
  const first = bills[0]!;
  const rest = bills.length - 1;
  return (
    <div className="flex items-center gap-1.5">
      <Link
        to="/procurement/bills/$billId"
        params={{ billId: first.id }}
        className="font-mono text-xs font-semibold text-sky-700 hover:underline"
      >
        {first.docNumber ?? "— draft —"}
      </Link>
      {rest > 0 ? (
        <span
          className="text-xs text-muted"
          title={bills
            .slice(1)
            .map((b) => b.docNumber)
            .join(", ")}
        >
          + {rest} more
        </span>
      ) : null}
    </div>
  );
}

// ── Filter select ─────────────────────────────────────────────────

/**
 * Shape mirrors `bills/list/bill-filter-bar.tsx`'s local `FilterSelect`
 * (h-10 trigger, icon inside, no external label) so the list-page
 * toolbars stay flush across the workspace. `active` keeps the existing
 * sky-tint cue when the filter is non-default — bills doesn't carry that
 * cue yet, but it's a useful "you have a filter applied" signal here.
 */
function FilterSelect({
  value,
  onValueChange,
  icon,
  ariaLabel,
  placeholder,
  widthClassName,
  active = false,
  children,
}: {
  value: string;
  onValueChange: (next: string) => void;
  icon: React.ReactNode;
  ariaLabel: string;
  placeholder: string;
  widthClassName: string;
  active?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger
        aria-label={ariaLabel}
        className={`h-10 shrink-0 gap-2 ${widthClassName} ${
          active ? "!border-sky-600 !text-sky-700 !font-semibold" : ""
        }`}
      >
        <span aria-hidden className="text-muted">
          {icon}
        </span>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>{children}</SelectContent>
    </Select>
  );
}

// ── Footer-bar helper ─────────────────────────────────────────────

/**
 * Return the most-frequent string in a list, with its occurrence count.
 * Empty / nullish entries are skipped. Returns null when the list has
 * no usable entries — callers render "—" in that case.
 */
function topByCount(
  values: ReadonlyArray<string | null | undefined>,
): { key: string; count: number } | null {
  const counts = new Map<string, number>();
  for (const v of values) {
    if (!v) continue;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  let best: { key: string; count: number } | null = null;
  for (const [key, count] of counts) {
    if (!best || count > best.count) best = { key, count };
  }
  return best;
}

// ── Vendor sub-line ───────────────────────────────────────────────

function vendorSubLine(p: {
  vendorCity: string | null;
  vendorTdsSection: string | null;
  mode: string;
  bills: Array<{ id: string; docNumber: string | null }>;
}): string {
  const parts: string[] = [];
  if (p.vendorCity) parts.push(p.vendorCity);
  // Surface the TDS section captured at payment time — useful at-a-glance
  // ("194I rent" / "194Q goods").
  if (p.vendorTdsSection) parts.push(`${p.vendorTdsSection}`);
  // For foreign vendors (no city, no TDS), describe by mode.
  if (parts.length === 0) {
    parts.push(p.mode === "card" ? "International · card" : "International");
  }
  return parts.join(" · ");
}

// ── Tiles + bits ──────────────────────────────────────────────────

type Accent = "indigo" | "emerald" | "amber" | "rose" | "muted";

const ACCENT: Record<Accent, { stripe: string; iconBg: string; iconText: string }> = {
  indigo: { stripe: "bg-sky-600", iconBg: "bg-sky-50", iconText: "text-sky-600" },
  emerald: { stripe: "bg-emerald-600", iconBg: "bg-emerald-50", iconText: "text-emerald-600" },
  amber: { stripe: "bg-amber-600", iconBg: "bg-amber-50", iconText: "text-amber-700" },
  rose: { stripe: "bg-rose-600", iconBg: "bg-rose-50", iconText: "text-rose-600" },
  muted: { stripe: "bg-line-2", iconBg: "bg-bg", iconText: "text-muted" },
};

interface KpiTileProps {
  accent: Accent;
  label: string;
  value: string;
  valueUnit?: string;
  meta?: React.ReactNode;
  icon: React.ReactNode;
}

function KpiTile({ accent, label, value, valueUnit, meta, icon }: KpiTileProps) {
  const a = ACCENT[accent];
  return (
    <div className="relative bg-paper border border-line rounded-md shadow-sm px-4 pt-3.5 pb-3 overflow-hidden transition-all hover:-translate-y-px hover:shadow-md hover:border-line-2">
      <span className={`absolute top-0 left-0 bottom-0 w-[3px] ${a.stripe}`} aria-hidden />
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted truncate">
          {label}
        </span>
        <span
          className={`inline-grid place-items-center w-[22px] h-[22px] rounded-sm ${a.iconBg} ${a.iconText}`}
        >
          {icon}
        </span>
      </div>
      <div className="text-2xl font-bold tracking-tight leading-none">
        <span className="num">{value}</span>
        {valueUnit ? (
          <span className="text-xs font-semibold text-muted ml-1">{valueUnit}</span>
        ) : null}
      </div>
      {meta ? (
        <div className="mt-1 text-xs text-muted flex items-center gap-1.5 flex-wrap">
          {meta}
        </div>
      ) : null}
    </div>
  );
}

function DeltaPill({ kind, label }: { kind: "up" | "dn"; label: string }) {
  const cls = kind === "up" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700";
  return (
    <span
      className={`inline-flex items-center px-1.5 py-px rounded-full text-xs font-semibold ${cls}`}
    >
      {label}
    </span>
  );
}

function Sep() {
  return <span className="inline-block w-[3px] h-[3px] rounded-full bg-line-2" aria-hidden />;
}

function StatusPill({ status }: { status: string }) {
  const palette: Record<string, { dot: string; bg: string; text: string; line?: string }> = {
    paid: { dot: "bg-emerald-600", bg: "bg-emerald-50", text: "text-emerald-700" },
    void: { dot: "bg-rose-600", bg: "bg-rose-50", text: "text-rose-700", line: "line-through" },
    awaiting_approval: { dot: "bg-amber-600", bg: "bg-amber-50", text: "text-amber-700" },
    approved: { dot: "bg-sky-600", bg: "bg-sky-50", text: "text-sky-700" },
    queued: { dot: "bg-line-2", bg: "bg-bg", text: "text-muted" },
  };
  const p = palette[status] ?? palette.queued!;
  const label = status === "awaiting_approval" ? "Awaiting" : status.replace(/_/g, " ");
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${p.bg} ${p.text} ${p.line ?? ""}`}
    >
      <span className={`w-[6px] h-[6px] rounded-full ${p.dot}`} aria-hidden />
      <span className="capitalize">{label}</span>
    </span>
  );
}

function EmptyState({ hasFilter }: { hasFilter: boolean }) {
  if (hasFilter) {
    return (
      <div className="bg-paper border border-line rounded-md shadow-sm py-10 text-center">
        <p className="text-base font-medium text-ink">No payments match these filters.</p>
        <p className="text-sm text-muted mt-1 max-w-md mx-auto">
          Widen the date range or clear the mode filter to see more.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-paper border border-line rounded-md shadow-sm py-10 text-center">
      <p className="text-base font-medium text-ink">No payments yet.</p>
      <p className="text-sm text-muted mt-1 max-w-md mx-auto">
        Open any approved or ready-to-pay bill and click "Record payment" to log what you paid the
        vendor. The payment will appear here, and the bill's status will flip to paid.
      </p>
      <Link
        to="/procurement/bills"
        className="inline-flex items-center mt-4 text-sm text-sky-700 hover:underline font-semibold"
      >
        Open bills →
      </Link>
    </div>
  );
}

// ── helpers ──────────────────────────────────────────────────────

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

function firstOfMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function formatRupees(paise: bigint): string {
  const rupees = paise / 100n;
  return `₹${rupees.toLocaleString("en-IN")}`;
}

function formatRupeesShort(paise: bigint): string {
  const rupees = paise / 100n;
  if (rupees >= 10_000_000n) return `₹${(Number(rupees) / 10_000_000).toFixed(2)}Cr`;
  if (rupees >= 100_000n) return `₹${(Number(rupees) / 100_000).toFixed(2)}L`;
  return `₹${rupees.toLocaleString("en-IN")}`;
}
